import axios from "axios";
import type {
  OrgDeferralPage,
  OrgDeferralsQuery,
  OrgScheduleDetail,
  RecognitionRunRow,
  RecognitionRunsResponse,
  RollForward,
  ScheduleReconciliation,
  UnpostedReleases,
} from "@/interfaces/subscriptionSchedules";
import { apiClient } from "@/lib/apiClient";

/** Drops empty params so an unset search isn't sent as "". */
function cleanQuery(q: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

/** The fiscal year's 24100 roll-forward. GFG-only. */
export async function fetchRollForward(fy: number): Promise<RollForward> {
  const { data } = await apiClient.get<{ success: boolean; data: RollForward }>(
    "/admin/subscription-schedules/summary",
    { params: { fy: String(fy) } },
  );
  return data.data;
}

/** Organizations with a deferred balance in the year — server-paged, searched and sorted. */
export async function fetchOrgDeferrals(query: OrgDeferralsQuery): Promise<OrgDeferralPage> {
  const { data } = await apiClient.get<{ success: boolean; data: OrgDeferralPage }>(
    "/admin/subscription-schedules/organizations",
    { params: cleanQuery(query) },
  );
  return data.data;
}

/** One organization's cycles and month lines. */
export async function fetchOrgScheduleDetail(
  organizationId: number,
  fy: number,
): Promise<OrgScheduleDetail> {
  const { data } = await apiClient.get<{ success: boolean; data: OrgScheduleDetail }>(
    `/admin/subscription-schedules/organizations/${organizationId}`,
    { params: { fy: String(fy) } },
  );
  return data.data;
}

/** What the schedules say is still owed against the books, as of a date (today when omitted). */
export async function fetchScheduleReconciliation(asOf?: string): Promise<ScheduleReconciliation> {
  const { data } = await apiClient.get<{ success: boolean; data: ScheduleReconciliation }>(
    "/admin/subscription-schedules/reconciliation",
    { params: cleanQuery({ asOf }) },
  );
  return data.data;
}

/** This month's recognition run and the run history, newest first. */
export async function fetchRecognitionRuns(
  page: number,
  limit: number,
): Promise<RecognitionRunsResponse> {
  const { data } = await apiClient.get<{ success: boolean; data: RecognitionRunsResponse }>(
    "/admin/subscription-schedules/runs",
    { params: { page: String(page), limit: String(limit) } },
  );
  return data.data;
}

/** Reruns recognition now. Rethrows with the server's message (e.g. a run already in progress). */
export async function rerunRecognition(): Promise<RecognitionRunRow> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: RecognitionRunRow }>(
      "/admin/subscription-schedules/runs",
    );
    return data.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data?.error) {
      throw new Error(String(err.response.data.error));
    }
    throw err;
  }
}

/** Releases up to a month that have not posted — what blocks closing it. */
export async function fetchUnpostedReleases(through: string): Promise<UnpostedReleases> {
  const { data } = await apiClient.get<{ success: boolean; data: UnpostedReleases }>(
    "/admin/subscription-schedules/unposted",
    { params: { through } },
  );
  return data.data;
}
